import { createFileRoute, useParams } from '@tanstack/react-router';
import { Link } from '@/lib/router-components';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { CertificateDto } from '@prometeus/contracts';

export const Route = createFileRoute('/certificacao/certificados/$id')({
  component: CertificadoPage,
});

const CIDADE_EMISSAO = 'Brasília';

function CertificadoPage() {
  const { id } = useParams({ from: '/certificacao/certificados/$id' });

  const { data: cert, isLoading } = useQuery<CertificateDto>({
    queryKey: ['certification-certificate', id],
    queryFn: async () => {
      const response = await api.get(`/certification/certificates/${id}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!id,
  });

  if (isLoading || !cert) {
    return (
      <div className="p-6 max-w-5xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className="animate-pulse h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
        <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    );
  }

  const issuedAt = cert.issuedAt ? new Date(cert.issuedAt) : null;
  const day = issuedAt ? issuedAt.getDate() : '';
  const month = issuedAt
    ? issuedAt.toLocaleDateString('pt-BR', { month: 'long' })
    : '';
  const year = issuedAt ? issuedAt.getFullYear() : '';
  const hoursText =
    cert.estimatedHours != null
      ? `${cert.estimatedHours} ${cert.estimatedHours === 1 ? 'hora' : 'horas'}`
      : '';
  const userName = cert.userName ?? 'O participante';
  const courseTitle = cert.courseTitle ?? 'Curso';

  const handleBaixarPdf = () => {
    if (cert.downloadUrl) {
      window.open(cert.downloadUrl, '_blank');
      return;
    }
    window.print();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto text-gray-900 dark:text-white certificado-page print:p-0" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <Link
        to="/certificacao"
        className="print:hidden text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 inline-block transition-colors"
      >
        ← Voltar à Certificação
      </Link>
      <div
        id="certificado-conteudo"
        className="rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden w-full print:shadow-none print:border-gray-300"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
      >
        {/* Faixa superior com identidade Prometeus */}
        <div className="bg-[#06141B] text-[#CCD0CF] px-8 py-6 flex flex-col sm:flex-row items-center justify-center gap-4">
          <img
            src="/logo-landing.svg"
            alt="Prometeus"
            className="h-10 w-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <span className="text-sm font-medium text-[#9BA8AB]">
            Prometeus Certificação
          </span>
        </div>

        <div className="p-8 sm:p-12 bg-white dark:bg-gray-800 text-[#06141B] dark:text-gray-100">
          <h1
            className="text-3xl sm:text-4xl font-semibold text-center text-[#06141B] dark:text-white mb-6 tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Certificado
          </h1>
          <div className="h-px w-16 mx-auto bg-[#253745]/30 dark:bg-gray-500 my-6" />

          <p className="text-[#253745] dark:text-gray-300 text-[15px] leading-relaxed mb-4 text-center max-w-3xl mx-auto">
            Certificamos, para os devidos fins, que <strong className="text-[#06141B] dark:text-white">{userName}</strong> concluiu com êxito o curso <strong className="text-[#06141B] dark:text-white">{courseTitle}</strong>, com carga horária total de {hoursText || '—'}, realizado por meio da plataforma Prometeus.
          </p>

          <p className="text-[#253745] dark:text-gray-300 text-[15px] leading-relaxed mb-8 text-center max-w-3xl mx-auto">
            Este certificado é emitido em {CIDADE_EMISSAO}, na data de {day} de {month} de {year}, sob o código de validação <span className="font-mono font-medium">{cert.uniqueCode}</span>.
          </p>

          {/* Assinatura */}
          <div className="flex flex-col items-center pt-6 border-t border-gray-200 dark:border-gray-600">
            <p
              className="text-2xl sm:text-3xl text-[#06141B] dark:text-white mb-0.5"
              style={{ fontFamily: "'Dancing Script', cursive" }}
            >
              Gabryel Rezende
            </p>
            <p className="text-xs font-medium text-[#253745] dark:text-gray-400 tracking-wider uppercase">
              CEO Prometeus
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center print:hidden">
        <button
          type="button"
          onClick={handleBaixarPdf}
          className="inline-flex px-5 py-2.5 rounded-full text-sm font-medium text-white bg-[#06141B] hover:bg-[#11212D] transition-colors shadow-md focus:outline-none focus:ring-0"
        >
          Baixar PDF
        </button>
      </div>
    </div>
  );
}
